import { mediaQuery } from '@design-system/styles'
import type { DefaultProps } from '@design-system/types'
import { css } from '@emotion/react'
import { s3URL } from '@mathflat/common/util'
import React, { type ReactNode } from 'react'
import { matchPath, type NavLink, useLocation } from 'react-router-dom'

import TabNavigationLink from '@/component/atoms/NavigationLink/TabNavigationLink'
import useRepository from '@/hooks/useRepository'

import PageTemplateService from './_service/PageTemplateService'

type NavInfo =
  | {
      label: ReactNode
      to: React.ComponentProps<NavLink>['to']
      /** 활성 판별에 쓸 경로. 한 탭이 여러 라우트를 포괄하면 배열로 전달한다. */
      matchUrl?: string | string[]
      onClick?: () => void
      restrictionMessage?: string
      isNew?: boolean
    }
  | false
  | undefined

const PageContentTemplate = ({ children }: DefaultProps) => {
  const pageTemplateService = useRepository(PageTemplateService)

  return (
    <div css={_Style} onClick={() => pageTemplateService.setSideNavigationOpen(false)}>
      {children}
    </div>
  )
}

export default PageContentTemplate

const WithTab = ({
  children,
  navInfoList,
  className,
}: { navInfoList: NavInfo[] } & DefaultProps) => {
  const location = useLocation()
  const pageTemplateService = useRepository(PageTemplateService)

  // 표시할 탭이 하나도 없으면 tab-list-container 자체를 그리지 않는다 (빈 줄 노출 방지)
  const hasVisibleTabs = navInfoList?.some(Boolean)

  return (
    <div
      css={_Style}
      className={className}
      onClick={() => pageTemplateService.setSideNavigationOpen(false)}
    >
      {hasVisibleTabs && (
        <div className="tab-list-container">
          {navInfoList.map((navInfo) => {
            if (!navInfo) return null

            // label, isNew는 ReactNode / 비표준 prop이므로 spread로 a 태그에 흘러가지 않도록 분리한다
            const { label, isNew, ...linkProps } = navInfo

            return (
              <TabNavigationLink
                className="tab-list-item"
                key={typeof navInfo.to === 'string' ? navInfo.to : JSON.stringify(navInfo.to)}
                {...linkProps}
              >
                <span css={S.tabLabelWrapper}>
                  {label}
                  {isNew && (
                    <img
                      src={s3URL.mathflatAssetsBucket.graphic('graphic-new.svg')}
                      alt="new"
                      width={14}
                      height={14}
                    />
                  )}
                </span>
                {matchPath(location.pathname, {
                  path:
                    navInfo.matchUrl ?? (typeof navInfo.to === 'string' ? navInfo.to : undefined),
                }) && (
                  <div className="underline-wrapper">
                    <div className="underline" />
                  </div>
                )}
              </TabNavigationLink>
            )
          })}
        </div>
      )}
      {children}
    </div>
  )
}

const Transparent = ({ children }: DefaultProps) => {
  const pageTemplateService = useRepository(PageTemplateService)

  return (
    <div
      css={_Style}
      className="transparent"
      onClick={() => pageTemplateService.setSideNavigationOpen(false)}
    >
      {children}
    </div>
  )
}

const FullPage = ({ children }: DefaultProps) => {
  const pageTemplateService = useRepository(PageTemplateService)

  return (
    <div
      css={_FullPageStyle}
      className="full-page"
      onClick={() => pageTemplateService.setSideNavigationOpen(false)}
    >
      <div className="full-inner-wrapper">{children}</div>
    </div>
  )
}

const FullPageInner = ({ children, style }: DefaultProps) => {
  return (
    <div className="inner" style={style}>
      {children}
    </div>
  )
}

const Fallback = () => {
  return (
    <>
      <div css={_SideSkeletonStyle} />
      <div css={_Style} />
    </>
  )
}

const S = {
  tabLabelWrapper: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
}

const _Style = css`
  display: flex;
  flex: auto;
  flex-direction: column;
  height: 100%;
  background: var(--White);
  max-width: 1920px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  border-radius: var(--Radius-200);
  overflow: hidden;
  border: 1px solid var(--Colors-Gray-400);

  &.transparent {
    background: transparent;
    border: none;
  }

  .tab-list-container {
    display: flex;
    padding: 4px 24px 0 24px;
    border-bottom: 1px solid var(--Colors-Gray-300);
  }

  .tab-list-item {
    position: relative;
    z-index: 5;
    min-width: 120px;
    padding: 0 16px;
  }

  .underline-wrapper {
    position: absolute;
    top: 0;
    left: 50%;
    width: 80px;
    height: 100%;
    transform: translateX(-50%);

    .underline {
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: var(--Colors-Gray-900);
    }
  }
`

const _FullPageStyle = css`
  display: flex;
  flex: auto;
  border: none;
  border-radius: 0;
  background: var(--Colors-Gray-200);
  overflow: auto;

  .full-inner-wrapper {
    display: flex;
    gap: 16px;
    width: 100%;
    max-width: 1920px;
    margin: 0 auto;
    padding: 16px 24px;
    overflow: auto;

    ${mediaQuery.large} {
      padding: 0;
    }
  }

  .inner {
    display: flex;
    border-radius: var(--Radius-200);
    height: max-content;
    width: 100%;
    overflow: hidden;
    border: 1px solid var(--Colors-Gray-400);

    ${mediaQuery.large} {
      border-radius: 0;
    }
  }

  ${mediaQuery.large} {
    padding: 0;
    border: none;
  }
`

const _SideSkeletonStyle = css`
  min-width: 198px;
  height: 100%;
  background: var(--White);
  border-radius: var(--Radius-200);
  border: 1px solid var(--Colors-Gray-400);

  ${mediaQuery.large} {
    display: none;
  }
`

PageContentTemplate.WithTab = WithTab
PageContentTemplate.Transparent = Transparent
PageContentTemplate.FullPage = FullPage
PageContentTemplate.FullPageInner = FullPageInner
PageContentTemplate.Fallback = Fallback
